import { useCallback, useEffect, useRef, useState } from 'react';

import Arrow from 'assets/images/icons/Arrow.png';
import CollapsedSideBarBody from 'components/common/chat-bubble/chat-bubble-side-bar/collapsed-side-bar-body';
import ExpandSideBarBody from 'components/common/chat-bubble/chat-bubble-side-bar/expand-side-bar-body';
import { useStoreDetails } from 'context/store-context';
import { debounce } from 'helper/controlled-execution';
import { isHttpsPresent } from 'helper/is-https-present';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import useWidgetPosition from 'hooks/useWidgetPosition';
import { isMobile } from 'react-device-detect';
import type { OtherWidget } from 'types';
import { OtherWidgetType } from 'types';

import classes from './chat-bubble-side-bar.module.scss';

interface IChatBubbleProps {
  chatEnabled: boolean;
  isAgentsOnline: boolean;
  onBubbleButtonClick: () => void;
  onAppointmentClick: () => void;
}
// eslint-disable-next-line sonarjs/cognitive-complexity
const ChatBubbleSideBar = ({
  chatEnabled,
  isAgentsOnline,
  onBubbleButtonClick,
  onAppointmentClick
}: IChatBubbleProps) => {
  const { widgetOption, onlineMenu, offlineMenu, theme, schedulingOption, otherWidgets, conciergeUrl, callQueuing } =
    useStoreDetails();
  const isChatEnabled = chatEnabled || (callQueuing && isAgentsOnline);
  const { log } = useActivityLogger();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [onlineButton, setOnlineButton] = useState<boolean>(true);
  const [offlineButton, setOfflineButton] = useState<boolean>(true);
  const otherWidgetsRef = useRef<Map<string, boolean>>(new Map());
  const isScrolled = useRef<boolean>(false);
  const toggleClickCaptureRef = useRef<Map<string, boolean>>(new Map());
  const isLeftPosiotion = useWidgetPosition();
  const collapseTimeoutRef = useRef<number | null>(null);
  const scrollCount = useRef<number>(1);

  const onClickToggle = () => {
    const toggleCapture = toggleClickCaptureRef.current.get('clicked');
    if (!toggleCapture) {
      toggleClickCaptureRef.current.set('clicked', true);
    } else {
      toggleClickCaptureRef.current.set('clicked', false);
    }
    setIsCollapsed(!isCollapsed);
  };

  const hideOtherWidget = (id: string) => {
    const otherWidget = document.querySelector(`#${id}, .${id}`);
    if (otherWidget && otherWidget instanceof HTMLElement) {
      otherWidgetsRef.current.set(id, false);
      otherWidget.style.display = 'none';
      otherWidget.style.visibility = 'hidden';
    }
  };

  const handleScroll = useCallback(
    debounce(() => {
      const toggleCapture = toggleClickCaptureRef.current.get('clicked');

      if (collapseTimeoutRef.current !== null) {
        clearTimeout(collapseTimeoutRef.current);
      }

      if (isMobile && !toggleCapture) {
        setIsCollapsed(true);
        if (scrollCount.current >= 2) {
          toggleClickCaptureRef.current.set('clicked', true);
        }

        collapseTimeoutRef.current = window.setTimeout(() => {
          setIsCollapsed(false);
          scrollCount.current++;
        }, 5000);
      }
    }, 100),
    [isMobile]
  );

  useEffect(() => {
    if (!isMobile && widgetOption.collapsed) {
      setIsCollapsed(false);
      return;
    }
    const timer = setTimeout(() => {
      const toggleCapture = toggleClickCaptureRef.current.get('clicked');
      if (!toggleCapture && !isScrolled.current) {
        setIsCollapsed(false);
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      otherWidgets.forEach(({ id, type, function: otherFunction }) => {
        if (type === OtherWidgetType.FUNCTION && otherFunction) {
          try {
            const otherWidgetFunction = new Function(otherFunction);
            otherWidgetFunction();
            otherWidgetsRef.current.set(otherFunction, false);
          } catch (error) {
            void log({
              action: Action.ERROR,
              description: `other widget function, error message: ${(error as Error).message}`,
              index: Index.ACTIVITY,
              label: 'ERROR'
            });
            console.error('other widget function: ', error);
          }
          return;
        }
        if (!id) return;
        hideOtherWidget(id);
      });
      if (otherWidgetsRef.current.size === otherWidgets.length) {
        clearInterval(interval);
      }
    }, 1000);
  }, [otherWidgets]);

  useEffect(() => {
    const handleScrollEven = () => {
      if (isScrolled.current !== true) {
        isScrolled.current = true;
      }

      handleScroll();
    };

    window.addEventListener('scroll', handleScrollEven);
    return () => {
      window.removeEventListener('scroll', () => handleScrollEven);
      handleScroll.cancel();
    };
  }, [handleScroll]);

  const onPressOfflineLink = () => {
    log({
      action: Action.OFFLINE_LINK_CLICKED_BY_CUSTOMER,
      description: 'customer click offline menu',
      label: 'CUSTOMER_WIDGET'
    });
  };

  // This function suppress other widgets and it works as toggle
  const toggleOtherWidget = (otherWidget: OtherWidget) => {
    const { id, function: otherFunction } = otherWidget;
    if (otherWidget.type === OtherWidgetType.FUNCTION && otherFunction) {
      try {
        const otherWidgetFunction = new Function(otherFunction);
        otherWidgetFunction();
        otherWidgetsRef.current.set(otherFunction, false);
      } catch (error) {
        void log({
          action: Action.ERROR,
          description: `other widget function, error message: ${(error as Error).message}`,
          index: Index.ACTIVITY,
          label: 'ERROR'
        });
        console.error('other widget function: ', error);
      }
      return;
    }
    if (!id) return;
    const currentElement = document.querySelector(`#${id}, .${id}`);

    if (currentElement && currentElement instanceof HTMLElement) {
      const shouldSuppress = currentElement.style.display !== 'none';
      otherWidgetsRef.current.set(id, shouldSuppress);
      currentElement.style.display = shouldSuppress ? 'none' : 'revert';
      currentElement.style.visibility = shouldSuppress ? 'hidden' : 'revert';
    }
  };

  function getWidgetStyle(isLeft: boolean, isCollapsed: boolean) {
    // Define common styles
    const commonStyles = {
      paddingBlock: isCollapsed ? '25px' : '17px',
      transition: 'width 0.1s',
      width: isCollapsed ? '34px' : '136px'
    };

    if (isLeft) {
      return {
        ...commonStyles,
        borderRadius: isCollapsed ? '0 18px 18px 0' : '0 81px 81px 0'
      };
    }
    return {
      ...commonStyles,
      borderRadius: isCollapsed ? '18px 0 0 18px' : '81px 0 0 81px'
    };
  }

  const onClickAvatar = () => {
    if (isChatEnabled) {
      onBubbleButtonClick();
    } else {
      if (conciergeUrl) {
        window.location.href = isHttpsPresent(conciergeUrl) ? conciergeUrl : 'https://' + conciergeUrl;
      }
    }
  };

  return (
    <div className={classes.root}>
      <div
        className={`${classes.cwWidget} ${isLeftPosiotion ? classes.left : classes.right}`}
        style={getWidgetStyle(isLeftPosiotion, isCollapsed)}
      >
        {!isCollapsed ? (
          <>
            <span
              onClick={onClickToggle}
              className={classes.widgetCurveBtn}
            >
              <img
                className={isLeftPosiotion ? classes.left : classes.right}
                src={Arrow}
                alt="arrow icon"
              />
            </span>
            <ExpandSideBarBody
              isLeftPosiotion={isLeftPosiotion}
              isChatEnabled={isChatEnabled}
              onlineMenu={onlineMenu}
              offlineMenu={offlineMenu}
              onlineButton={onlineButton}
              offlineButton={offlineButton}
              otherWidgets={otherWidgets}
              otherWidgetsRef={otherWidgetsRef}
              schedulingOption={schedulingOption}
              theme={theme}
              widgetOption={widgetOption}
              onBubbleButtonClick={onBubbleButtonClick}
              onAppointmentClick={onAppointmentClick}
              onPressOfflineLink={onPressOfflineLink}
              onClickAvatar={onClickAvatar}
              setOnlineButton={setOnlineButton}
              setOfflineButton={setOfflineButton}
              toggleOtherWidget={toggleOtherWidget}
            />
          </>
        ) : (
          <CollapsedSideBarBody
            isLeftPosiotion={isLeftPosiotion}
            isChatEnabled={isChatEnabled}
            onlineMenu={onlineMenu}
            offlineMenu={offlineMenu}
            onlineButton={onlineButton}
            offlineButton={offlineButton}
            otherWidgets={otherWidgets}
            otherWidgetsRef={otherWidgetsRef}
            schedulingOption={schedulingOption}
            widgetOption={widgetOption}
            onBubbleButtonClick={onBubbleButtonClick}
            onAppointmentClick={onAppointmentClick}
            onPressOfflineLink={onPressOfflineLink}
            onClickAvatar={onClickAvatar}
            onClickToggle={onClickToggle}
            setOnlineButton={setOnlineButton}
            setOfflineButton={setOfflineButton}
            toggleOtherWidget={toggleOtherWidget}
          />
        )}
      </div>
    </div>
  );
};

export default ChatBubbleSideBar;
